import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { security, USER_ROLES } from '../config/security'
import './DashboardV2.css'

const PERIODOS = {
  DIA: 'dia',
  SEMANA: 'semana',
  MES: 'mes',
  PERSONALIZADO: 'personalizado'
}

const canSeeAllCompany = (role) =>
  [USER_ROLES.ADMINISTRADOR, USER_ROLES.GERENTE, USER_ROLES.ADMINISTRATIVO, USER_ROLES.GERENTE_COMERCIAL].includes(role)

const METRICS_CONFIG = [
  { key: 'instalacoes', label: 'Instalações', icon: '⚡', color: '#1565c0', bg: 'rgba(21, 101, 192, 0.12)' },
  { key: 'clientes', label: 'Clientes', icon: '👥', color: '#7b1fa2', bg: 'rgba(123, 31, 162, 0.12)' },
  { key: 'vistorias', label: 'Vistorias', icon: '📋', color: '#e65100', bg: 'rgba(230, 81, 0, 0.12)' },
  { key: 'visitas', label: 'Visitas', icon: '📅', color: '#00838f', bg: 'rgba(0, 131, 143, 0.12)' },
  { key: 'leads', label: 'Leads', icon: '🎯', color: '#c2185b', bg: 'rgba(194, 24, 91, 0.12)' },
  { key: 'propostas', label: 'Propostas', icon: '📄', color: '#5d4037', bg: 'rgba(93, 64, 55, 0.12)' }
]

const DashboardV2 = ({ onBack, userRole, userEmpresa, userId }) => {
  const [periodo, setPeriodo] = useState(PERIODOS.MES)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [showFiltros, setShowFiltros] = useState(false)
  const [loading, setLoading] = useState(true)
  const [dados, setDados] = useState({
    faturamento: 0,
    instalacoes: 0,
    clientes: 0,
    vistorias: 0,
    visitas: 0,
    leads: 0,
    propostas: 0
  })

  const getDateRange = useCallback(() => {
    const hoje = new Date()
    let inicio, fim

    switch (periodo) {
      case PERIODOS.DIA:
        inicio = new Date(hoje)
        fim = new Date(hoje)
        break
      case PERIODOS.SEMANA:
        const diaSemana = hoje.getDay()
        const diff = diaSemana === 0 ? 6 : diaSemana - 1
        inicio = new Date(hoje)
        inicio.setDate(hoje.getDate() - diff)
        inicio.setHours(0, 0, 0, 0)
        fim = new Date(inicio)
        fim.setDate(inicio.getDate() + 6)
        fim.setHours(23, 59, 59, 999)
        break
      case PERIODOS.MES:
        inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
        fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0, 23, 59, 59, 999)
        break
      case PERIODOS.PERSONALIZADO:
        if (!dataInicio || !dataFim) return null
        inicio = new Date(dataInicio + 'T00:00:00')
        fim = new Date(dataFim + 'T23:59:59')
        break
      default:
        inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
        fim = new Date(hoje)
    }
    return { inicio, fim }
  }, [periodo, dataInicio, dataFim])

  const formatDateForDb = (d) => d.toISOString().split('T')[0]
  const formatTimestampForDb = (d) => d.toISOString()

  const loadDados = useCallback(async () => {
    const range = getDateRange()
    if (!range) {
      setLoading(false)
      return
    }
    const { inicio, fim } = range
    const inicioStr = formatDateForDb(inicio)
    const fimStr = formatDateForDb(fim)
    const inicioTs = formatTimestampForDb(inicio)
    const fimTs = formatTimestampForDb(fim)

    const isAdmin = userRole === USER_ROLES.ADMINISTRADOR
    const seeAllCompany = canSeeAllCompany(userRole)
    const empresaId = userEmpresa?.id

    setLoading(true)
    try {
      const result = {
        faturamento: 0,
        instalacoes: 0,
        clientes: 0,
        vistorias: 0,
        visitas: 0,
        leads: 0,
        propostas: 0
      }

      let qFaturamento = supabase
        .from('clientes_cadastro')
        .select('valor_total')
        .gte('data_cadastro', inicioStr)
        .lte('data_cadastro', fimStr)
      if (!isAdmin && empresaId) qFaturamento = qFaturamento.eq('empresa_id', empresaId)
      const { data: fatData } = await qFaturamento
      result.faturamento = (fatData || []).reduce((s, r) => s + (Number(r.valor_total) || 0), 0)

      let qInstalacoes = supabase
        .from('clientes')
        .select('id', { count: 'exact', head: true })
        .gte('data_instalacao', inicioStr)
        .lte('data_instalacao', fimStr)
      if (!isAdmin && empresaId) qInstalacoes = qInstalacoes.eq('empresa_id', empresaId)
      const { count: countInst } = await qInstalacoes
      result.instalacoes = countInst ?? 0

      let qClientes = supabase
        .from('clientes_cadastro')
        .select('id', { count: 'exact', head: true })
        .gte('data_cadastro', inicioStr)
        .lte('data_cadastro', fimStr)
      if (!isAdmin && empresaId) qClientes = qClientes.eq('empresa_id', empresaId)
      const { count: countClientes } = await qClientes
      result.clientes = countClientes ?? 0

      const { data: tipoVistoria } = await supabase
        .from('tipo_servico')
        .select('id')
        .eq('nome', 'Vistoria')
        .maybeSingle()

      if (tipoVistoria?.id) {
        let qVistorias = supabase
          .from('ordens_servicos')
          .select('id', { count: 'exact', head: true })
          .eq('id_tipo_servico', tipoVistoria.id)
          .gte('data_criacao', inicioTs)
          .lte('data_criacao', fimTs)
        if (!isAdmin && empresaId) qVistorias = qVistorias.eq('id_empresa', empresaId)
        const { count: countVist } = await qVistorias
        result.vistorias = countVist ?? 0
      }

      if (isAdmin) {
        const { count: countVisitas } = await supabase
          .from('eventos_calendario')
          .select('id', { count: 'exact', head: true })
          .gte('data_hora', inicioTs)
          .lte('data_hora', fimTs)
        result.visitas = countVisitas ?? 0
      } else if (seeAllCompany && empresaId) {
        const { data: usuariosEmpresa } = await supabase
          .from('usuarios_sistema')
          .select('id')
          .eq('empresa_id', empresaId)
        const ids = (usuariosEmpresa || []).map(u => u.id)
        if (ids.length > 0) {
          const { count: countVisitas } = await supabase
            .from('eventos_calendario')
            .select('id', { count: 'exact', head: true })
            .gte('data_hora', inicioTs)
            .lte('data_hora', fimTs)
            .in('id_responsavel', ids)
          result.visitas = countVisitas ?? 0
        }
      } else if (userId) {
        const { count: countVisitas } = await supabase
          .from('eventos_calendario')
          .select('id', { count: 'exact', head: true })
          .gte('data_hora', inicioTs)
          .lte('data_hora', fimTs)
          .eq('id_responsavel', userId)
        result.visitas = countVisitas ?? 0
      }

      let qLeads = supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .gte('data_contato', inicioStr)
        .lte('data_contato', fimStr)
      if (!isAdmin && empresaId) {
        qLeads = qLeads.eq('empresa_id', empresaId)
        if (!seeAllCompany && userId) qLeads = qLeads.eq('responsavel', userId)
      }
      const { count: countLeads } = await qLeads
      result.leads = countLeads ?? 0

      if (isAdmin) {
        const { count: c } = await supabase
          .from('propostas_clientes')
          .select('id', { count: 'exact', head: true })
          .gte('data_proposta', inicioTs)
          .lte('data_proposta', fimTs)
        result.propostas = c ?? 0
      } else if (seeAllCompany && empresaId) {
        const { data: propData } = await supabase
          .from('propostas_clientes')
          .select('id, usuarios_sistema!id_usuario_responsavel(empresa_id)')
          .gte('data_proposta', inicioTs)
          .lte('data_proposta', fimTs)
        const filtradas = (propData || []).filter(p => p.usuarios_sistema?.empresa_id === empresaId)
        result.propostas = filtradas.length
      } else {
        const { count: c } = await supabase
          .from('propostas_clientes')
          .select('id', { count: 'exact', head: true })
          .gte('data_proposta', inicioTs)
          .lte('data_proposta', fimTs)
          .eq('id_usuario_responsavel', userId)
        result.propostas = c ?? 0
      }

      setDados(result)
    } catch (err) {
      console.error('Erro ao carregar Dashboard V2:', err)
    } finally {
      setLoading(false)
    }
  }, [getDateRange, userRole, userEmpresa?.id, userId])

  useEffect(() => {
    loadDados()
  }, [loadDados])

  const formatarMoeda = (v) =>
    Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })

  const chartData = METRICS_CONFIG.map(m => ({
    ...m,
    value: dados[m.key] ?? 0
  }))

  const maxBar = Math.max(1, ...chartData.map(d => d.value))
  const totalAtividades = chartData.reduce((s, d) => s + d.value, 0)

  return (
    <div className="dash-v2">
      <header className="dash-v2-hero">
        <div className="dash-v2-hero-bg" />
        <div className="dash-v2-hero-top">
          <button type="button" className="dash-v2-back" onClick={onBack} aria-label="Voltar">
            ← Voltar
          </button>
          <button
            type="button"
            className="dash-v2-btn-filtro"
            onClick={() => setShowFiltros(true)}
            aria-label="Abrir filtros"
            title="Abrir filtros"
          >
            <span className="dash-v2-btn-filtro-icon">⚙</span>
            <span>Filtros</span>
          </button>
        </div>
        <h1>Dashboard</h1>
        <p>Visão geral dos seus indicadores</p>
      </header>

      <div className={`dash-v2-filtros-overlay ${showFiltros ? 'is-open' : ''}`} onClick={() => setShowFiltros(false)} aria-hidden={!showFiltros} />
      <aside className={`dash-v2-filtros-panel ${showFiltros ? 'is-open' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="dash-v2-filtros-header">
          <h3>Filtros</h3>
          <button type="button" className="dash-v2-filtros-close" onClick={() => setShowFiltros(false)} aria-label="Fechar">×</button>
        </div>
        <div className="dash-v2-filtros-body">
          <div className="dash-v2-filter-group">
            <label>Período</label>
            <select value={periodo} onChange={(e) => setPeriodo(e.target.value)}>
              <option value={PERIODOS.DIA}>Hoje</option>
              <option value={PERIODOS.SEMANA}>Esta semana</option>
              <option value={PERIODOS.MES}>Este mês</option>
              <option value={PERIODOS.PERSONALIZADO}>Personalizado</option>
            </select>
          </div>
          {periodo === PERIODOS.PERSONALIZADO && (
            <div className="dash-v2-dates">
              <label>Data início</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                aria-label="Data início"
              />
              <label>Data fim</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                aria-label="Data fim"
              />
            </div>
          )}
        </div>
      </aside>

      {loading ? (
        <div className="dash-v2-loading">
          <div className="dash-v2-spinner" />
          <span>Carregando indicadores...</span>
        </div>
      ) : (
        <main className="dash-v2-main">
          <section className="dash-v2-kpi-hero">
            <div className="dash-v2-kpi-faturamento">
              <span className="dash-v2-kpi-label">Faturamento</span>
              <span className="dash-v2-kpi-valor">{formatarMoeda(dados.faturamento)}</span>
            </div>
          </section>

          <section className="dash-v2-grid">
            <article className="dash-v2-card dash-v2-chart-card">
              <h3>Atividades por categoria</h3>
              <div className="dash-v2-bars">
                {chartData.map((item) => (
                  <div key={item.key} className="dash-v2-bar-row">
                    <div className="dash-v2-bar-label">
                      <span className="dash-v2-bar-icon" style={{ backgroundColor: item.bg, color: item.color }}>{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                    <div className="dash-v2-bar-track">
                      <div
                        className="dash-v2-bar-fill"
                        style={{
                          width: `${(item.value / maxBar) * 100}%`,
                          backgroundColor: item.color
                        }}
                      />
                    </div>
                    <span className="dash-v2-bar-value">{item.value}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="dash-v2-card dash-v2-donut-card">
              <h3>Distribuição</h3>
              <div className="dash-v2-donut-wrap">
                <svg className="dash-v2-donut" viewBox="0 0 120 120">
                  {totalAtividades > 0 ? (
                    (() => {
                      let offset = 0
                      const circumference = 2 * Math.PI * 45
                      return chartData.filter(d => d.value > 0).map((item) => {
                        const pct = item.value / totalAtividades
                        const segment = pct * circumference
                        const dashOffset = -offset * circumference
                        offset += pct
                        return (
                          <circle
                            key={item.key}
                            cx="60"
                            cy="60"
                            r="45"
                            fill="none"
                            stroke={item.color}
                            strokeWidth="22"
                            strokeDasharray={`${segment} ${circumference}`}
                            strokeDashoffset={dashOffset}
                            transform="rotate(-90 60 60)"
                          />
                        )
                      })
                    })()
                  ) : (
                    <circle cx="60" cy="60" r="45" fill="none" stroke="#e9ecef" strokeWidth="22" />
                  )}
                </svg>
                <div className="dash-v2-donut-center">
                  <span className="dash-v2-donut-total">{totalAtividades}</span>
                  <span className="dash-v2-donut-sub">total</span>
                </div>
              </div>
              <ul className="dash-v2-legend">
                {chartData.filter(d => d.value > 0).map((item) => (
                  <li key={item.key}>
                    <span className="dash-v2-legend-dot" style={{ backgroundColor: item.color }} />
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </article>
          </section>

          <section className="dash-v2-mini-cards">
            {chartData.map((item) => (
              <div key={item.key} className="dash-v2-mini-card" style={{ borderColor: item.color }}>
                <span className="dash-v2-mini-icon" style={{ backgroundColor: item.bg, color: item.color }}>{item.icon}</span>
                <div className="dash-v2-mini-content">
                  <span className="dash-v2-mini-value">{item.value}</span>
                  <span className="dash-v2-mini-label">{item.label}</span>
                </div>
              </div>
            ))}
          </section>
        </main>
      )}
    </div>
  )
}

export default DashboardV2
